// JWT decoder utility
export const decodeJWT = (token) => {
  try {
    if (!token) return null
    
    // JWT has 3 parts separated by dots: header.payload.signature
    const parts = token.split('.')
    if (parts.length !== 3) return null
    
    // Decode the payload (middle part)
    const payload = parts[1]
    
    // Add padding if needed (JWT base64 encoding doesn't include padding)
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4)
    
    // Decode from base64
    const decodedPayload = atob(paddedPayload)
    
    // Parse JSON
    const parsed = JSON.parse(decodedPayload)
    
    return parsed
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return null
  }
}

// Extract user information from JWT claims
export const extractUserFromJWT = (token) => {
  const decoded = decodeJWT(token)
  if (!decoded) return null
  
  // Common JWT claim mappings - including Reziland-specific claims
  return {
    id: decoded.sub || 
        decoded.userId || 
        decoded.id || 
        decoded.jti || 
        decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
        
    email: decoded.email || 
           decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'],
           
    userName: decoded.preferred_username || 
              decoded.username || 
              decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
              
    firstName: decoded.given_name || 
               decoded.firstName || 
               decoded.FirstName || // Reziland uses capital F
               decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'],
               
    lastName: decoded.family_name || 
              decoded.lastName || 
              decoded.LastName || // Reziland uses capital L
              decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'],
              
    name: decoded.name || 
          decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
          
    phoneNumber: decoded.phone_number || 
                 decoded.phoneNumber || 
                 decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/mobilephone'],
                 
    profilePictureUrl: decoded.ProfilePictureUrl || 
                       decoded.profilePictureUrl || 
                       decoded.picture,
                       
    phoneNumberConfirmed: decoded.PhoneNumberConfirmed === 'True' || 
                          decoded.phoneNumberConfirmed === true,
                          
    emailConfirmed: decoded.EmailConfirmed === 'True' || 
                    decoded.emailConfirmed === true,
                    
    roles: decoded.role || 
           decoded.roles || 
           decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 
           [],
    
    // Token metadata
    iat: decoded.iat, // issued at
    exp: decoded.exp, // expires at
    iss: decoded.iss, // issuer
    aud: decoded.aud, // audience
    
    // Raw decoded token for debugging
    _raw: decoded
  }
}

// Check if token is expired
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp) return true
  
  const currentTime = Math.floor(Date.now() / 1000)
  return decoded.exp < currentTime
}

// Get token expiration time
export const getTokenExpiration = (token) => {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp) return null
  
  return new Date(decoded.exp * 1000)
}

// Check if token expires soon (within next 5 minutes)
export const isTokenExpiringSoon = (token, minutesThreshold = 5) => {
  const decoded = decodeJWT(token)
  if (!decoded || !decoded.exp) return true
  
  const currentTime = Math.floor(Date.now() / 1000)
  const thresholdTime = currentTime + (minutesThreshold * 60)
  
  return decoded.exp < thresholdTime
}